import DigiobcanApi from '../../../api/DigiobcanApi';
import { useLogger } from '../../../providers/LoggerProvider';
import { __, toastError } from '../../../utils';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import ListSubheader from '@mui/material/ListSubheader';
import { Button, Menu, MenuItem, Stack } from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Parametre, ktorými je zoznam práve vyfiltrovaný - presne to, čo stránka
 * posiela do `adminGetReports`, len bez `limit`.
 */
export type ReportsExportQuery = Omit<
  Parameters<typeof DigiobcanApi.adminExportReports>[0],
  'format' | 'mode'
>;

type ExportChoice = {
  key: string;
  format: 'pdf' | 'csv';
  mode: 'simple' | 'extended';
  label: string;
  hint: string;
};

/**
 * Štyri položky, nie štyri tlačidlá: tlač je akcia, ktorá sa robí občas,
 * a štyri tlačidlá v hlavičke by trvalo zaberali miesto vedľa titulku.
 *
 * Režimy sú **dva** a sú z [#5]: jednoduchý je prehľad tak, ako vyzerá zoznam,
 * rozšírený k nemu pridáva popis podnetu a vyjadrenie úradu. Formáty sú tiež
 * dva a nie sú to dve cesty k tomu istému - PDF je na tlač a do spisu, CSV na
 * ďalšie spracovanie v Exceli.
 */
const CHOICES: ExportChoice[] = [
  {
    key: 'pdf-simple',
    format: 'pdf',
    mode: 'simple',
    label: __('Prehľad podnetov'),
    hint: __('Tabuľka ako v zozname'),
  },
  {
    key: 'pdf-extended',
    format: 'pdf',
    mode: 'extended',
    label: __('Rozšírený prehľad'),
    hint: __('Aj popis podnetu a vyjadrenie úradu'),
  },
  {
    key: 'csv-simple',
    format: 'csv',
    mode: 'simple',
    label: __('Prehľad podnetov'),
    hint: __('Tabuľka ako v zozname'),
  },
  {
    key: 'csv-extended',
    format: 'csv',
    mode: 'extended',
    label: __('Rozšírený prehľad'),
    hint: __('Aj popis podnetu a vyjadrenie úradu'),
  },
];

/**
 * Tlač a export zoznamu podnetov.
 *
 * **Sadzbu robí server, nie prehliadač.** Do septembra 2026 tu bolo tlačidlo,
 * ktoré volalo `window.print()`, teda tlačilo stránku aplikácie - so shellom,
 * so skrolovacími kontajnermi a hlavne **len s tou stranou zoznamu, ktorá bola
 * práve zobrazená**. Výtlačok tak tvrdil nepravdu o rozsahu agendy a texty
 * v ňom boli rozbité (viď [#6]). Dokument dnes skladá `digiobcan-api`
 * (`GET /admin/reports/export`) z **celého vyfiltrovaného zoznamu**, rovnako
 * ako report hlasovania.
 *
 * Odpoveď je binárna, takže volanie ide **mimo `useM`**: generovaný klient pri
 * inom `Content-Type` než JSON vráti rovno `Response`, z ktorého sa spraví
 * `Blob` a odkaz. Tá istá cesta ako pri PDF reportu hlasovania.
 */
const ReportsExportButton: React.FC<{
  query: ReportsExportQuery;
  /** Počet podnetov na strane - len na to, či sa má tlač vôbec ponúkať. */
  disabled?: boolean;
}> = ({ query, disabled }) => {
  const { logger } = useLogger();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [downloading, setDownloading] = React.useState<string | null>(null);

  const handleExport = async (choice: ExportChoice) => {
    setAnchorEl(null);

    if (!navigator.onLine) {
      toastError(__('Nie ste pripojený k internetu.'));
      return;
    }

    setDownloading(choice.key);
    try {
      const response = (await DigiobcanApi.adminExportReports({
        ...query,
        format: choice.format,
        mode: choice.mode,
      })) as unknown as Response;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Meno súboru nesie server v `Content-Disposition`, ale `download`
      // ho prebíja - a bez neho by prehliadač pri `blob:` adrese ponúkol
      // náhodný reťazec. Dátum je tu preto zopakovaný.
      link.download = `podnety-${new Date().toISOString().slice(0, 10)}${
        choice.mode === 'extended' ? '-rozsireny' : ''
      }.${choice.format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      logger.error('Error exporting reports', {
        action: 'exportReports',
        error: e,
        data: { format: choice.format, mode: choice.mode },
      });
      toastError(
        // Najpravdepodobnejšia chyba je strop exportu, a tá sa dá vyriešiť
        // jediným krokom - preto je v hláške, nie len „nepodarilo sa".
        __(
          'Zoznam sa nepodarilo pripraviť. Ak je podnetov veľmi veľa, zúžte filter.',
        ),
      );
    } finally {
      setDownloading(null);
    }
  };

  const item = (choice: ExportChoice) => (
    <MenuItem
      key={choice.key}
      disabled={!!downloading}
      onClick={() => handleExport(choice)}
      sx={{ alignItems: 'flex-start', py: 1 }}
    >
      <Stack gap={0.25}>
        <span>{choice.label}</span>
        <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>{choice.hint}</span>
      </Stack>
    </MenuItem>
  );

  return (
    <>
      <Button
        variant='outline'
        size='small'
        disabled={disabled}
        loading={!!downloading}
        startIcon={<PrintOutlinedIcon fontSize='small' />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {__('Tlačiť')}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 280 } } }}
      >
        {/* Nadpisy skupín, nie štyri položky za sebou: rozdiel medzi PDF
            a CSV je rozdiel v tom, načo dokument je, nie v jeho obsahu. */}
        <ListSubheader
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            lineHeight: 2.2,
          }}
        >
          <DescriptionOutlinedIcon fontSize='small' />
          {__('Na tlač (PDF)')}
        </ListSubheader>
        {CHOICES.filter((choice) => choice.format === 'pdf').map(item)}

        <ListSubheader
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            lineHeight: 2.2,
          }}
        >
          <TableChartOutlinedIcon fontSize='small' />
          {__('Do Excelu (CSV)')}
        </ListSubheader>
        {CHOICES.filter((choice) => choice.format === 'csv').map(item)}
      </Menu>
    </>
  );
};

export default ReportsExportButton;
